import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Download, Loader2, CheckCircle2 } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { MotionReveal } from '../../components/strategy/Motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Teaser = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const [form, setForm] = useState({ name: '', email: '', organization: '', audience: '' });
  const [submitting, setSubmitting] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrorMsg('');
    try {
      const res = await axios.post(`${API}/strategy/teaser-request`, form, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'berkshire-500b-bet-on-africa.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setDownloaded(true);
    } catch (err) {
      setErrorMsg('Could not generate the briefing pack. Please try again or email info@berkshire.inc.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="App font-sans bg-[#0a1729] text-white min-h-screen">
      <Header onPartnerClick={() => {}} />

      <section className="pt-32 pb-16 px-6">
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{
          backgroundImage: 'linear-gradient(#2f9e8e 1px, transparent 1px), linear-gradient(90deg, #2f9e8e 1px, transparent 1px)',
          backgroundSize: '80px 80px'
        }} />
        <div className="max-w-6xl mx-auto relative">
          <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-8" data-testid="teaser-back-link">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>

          <div className="grid lg:grid-cols-12 gap-12 items-start">
            {/* LEFT: pitch */}
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.2em] uppercase text-[#7fd6c9] mb-5">
                <span className="w-8 h-px bg-[#7fd6c9]/60" /> Executive Briefing Pack
              </div>
              <h1 className="font-serif text-4xl md:text-6xl font-bold leading-[1.05] mb-6">
                The $500 Billion Bet on Africa — <span className="text-[#7fd6c9]">in 4 pages.</span>
              </h1>
              <p className="text-lg text-white/75 leading-relaxed mb-8 max-w-2xl">
                A concise, forwardable PDF summarising BIG&apos;s 20-year America–Africa strategic infrastructure thesis. Ideal for circulating internally before a briefing call with our partners.
              </p>

              {/* What's inside */}
              <div className="space-y-3 mb-10">
                {[
                  { p: '1', t: 'The $500B Thesis', d: 'Headline targets, the core insight, and the America-Africa bridge' },
                  { p: '2', t: 'The 8 Strategic Platforms', d: 'Energy · Critical Minerals · Infrastructure · Industrial · Commodities · Digital · Capital · Sovereign' },
                  { p: '3', t: 'Capital Architecture & Roadmap', d: 'Sources / uses of the $500B and the 5-phase 2026–2046 roadmap with phase KPIs' },
                  { p: '4', t: 'Engage', d: 'Tailored pathways for capital partners, sovereign partners and U.S. corporate partners' },
                ].map((s, i) => (
                  <MotionReveal key={s.p} delay={i * 90}>
                    <div className="flex items-start gap-4 p-4 bg-white/[0.04] border border-white/10 rounded-lg hover:border-[#2f9e8e]/40 hover:bg-white/[0.06] transition-all">
                      <div className="w-10 h-10 rounded-lg bg-[#2f9e8e]/20 flex items-center justify-center flex-shrink-0">
                        <span className="text-[#7fd6c9] font-bold">{s.p}</span>
                      </div>
                      <div>
                        <div className="font-serif text-base font-bold text-white">{s.t}</div>
                        <div className="text-sm text-white/60 mt-0.5">{s.d}</div>
                      </div>
                    </div>
                  </MotionReveal>
                ))}
              </div>

              <p className="text-xs text-white/40 max-w-xl leading-relaxed">
                For general informational purposes only. Not an offer of securities, financial advice, a commitment of capital or a guarantee of investment performance.
              </p>
            </div>

            {/* RIGHT: form */}
            <div className="lg:col-span-5 lg:sticky lg:top-28">
              <div className="bg-gradient-to-br from-white/[0.07] to-white/[0.02] border border-white/10 rounded-2xl p-7 shadow-2xl">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 rounded-xl bg-[#2f9e8e] flex items-center justify-center">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="font-serif text-xl font-bold">Download the briefing pack</h2>
                    <p className="text-xs text-white/55">PDF · 4 pages</p>
                  </div>
                </div>

                {downloaded ? (
                  <div className="py-8 text-center" data-testid="teaser-success">
                    <CheckCircle2 className="w-12 h-12 text-[#7fd6c9] mx-auto mb-3" />
                    <h3 className="font-serif text-lg font-bold mb-2">Download started</h3>
                    <p className="text-sm text-white/70 mb-5">Check your downloads folder for <code className="text-[#7fd6c9]">berkshire-500b-bet-on-africa.pdf</code>.</p>
                    <button onClick={() => { setDownloaded(false); setForm({ name: '', email: '', organization: '', audience: '' }); }}
                      className="text-sm text-[#7fd6c9] hover:text-white" data-testid="teaser-reset-btn">
                      Download again →
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4" data-testid="teaser-form">
                    {errorMsg && (
                      <div className="bg-red-500/10 border border-red-500/30 text-red-200 text-sm rounded-lg px-4 py-3" data-testid="teaser-error">{errorMsg}</div>
                    )}

                    <div>
                      <label className="block text-xs font-medium text-white/60 mb-1.5 uppercase tracking-wider">Full Name *</label>
                      <input type="text" name="name" required value={form.name} onChange={handleChange}
                        className="w-full bg-[#0a1729] border border-white/15 text-white px-4 py-3 rounded-lg focus:outline-none focus:border-[#2f9e8e] placeholder-white/30"
                        placeholder="Jane Doe" data-testid="teaser-input-name" />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-white/60 mb-1.5 uppercase tracking-wider">Email *</label>
                      <input type="email" name="email" required value={form.email} onChange={handleChange}
                        className="w-full bg-[#0a1729] border border-white/15 text-white px-4 py-3 rounded-lg focus:outline-none focus:border-[#2f9e8e] placeholder-white/30"
                        placeholder="jane@office.com" data-testid="teaser-input-email" />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-white/60 mb-1.5 uppercase tracking-wider">Organization</label>
                      <input type="text" name="organization" value={form.organization} onChange={handleChange}
                        className="w-full bg-[#0a1729] border border-white/15 text-white px-4 py-3 rounded-lg focus:outline-none focus:border-[#2f9e8e] placeholder-white/30"
                        placeholder="Institution / company" data-testid="teaser-input-org" />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-white/60 mb-1.5 uppercase tracking-wider">Audience</label>
                      <select name="audience" value={form.audience} onChange={handleChange}
                        className="w-full bg-[#0a1729] border border-white/15 text-white px-4 py-3 rounded-lg focus:outline-none focus:border-[#2f9e8e]"
                        data-testid="teaser-input-audience">
                        <option value="">I am a...</option>
                        <option value="capital">Capital Partner (Investor)</option>
                        <option value="sovereign">Sovereign Partner (Government)</option>
                        <option value="corporate">U.S. Corporate Partner</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <button type="submit" disabled={submitting}
                      className="w-full bg-[#2f9e8e] hover:bg-[#268578] text-white py-3.5 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-70 mt-2"
                      data-testid="teaser-submit-btn">
                      {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> Generating PDF...</> : <><Download className="w-5 h-5" /> Download Briefing Pack</>}
                    </button>

                    <p className="text-[10px] text-white/40 text-center">
                      Submitting will generate the PDF and notify our team. We may follow up with next steps.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Teaser;
